import type { MovieCard as MovieCardModel } from '@zyx/shared';
import { Info, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMovie } from '@/hooks/use-movies';
import { editionLabel, formatRuntime } from '@/lib/format';
import { urls } from '@/lib/urls';
import { AssetImage } from './asset';

/**
 * The movie the home page opens on: its artwork across the top, shaded so the words
 * read, with what the library knows of it and a Play that starts it (or resumes it).
 */
export function FeaturedHero(props: { readonly movie: MovieCardModel }) {
  const card = props.movie;
  // The card has no description, cast or genres; the detail does.
  const detail = useMovie(card.code).data?.movie;
  const genres = detail?.terms.filter((term) => term.kind === 'genre').slice(0, 3) ?? [];
  const resuming = card.progress !== undefined && !card.progress.finished && card.progress.positionS > 0;
  const facts = [card.releaseDate?.slice(0, 4), formatRuntime(card.durationS), card.editions.map(editionLabel).join(' / ')].filter((part) => part !== undefined && part !== '');

  return (
    <section className="relative -mx-4 -mt-6 overflow-hidden sm:-mx-6 lg:-mx-8" aria-label={`Featured: ${card.title}`}>
      <div className="relative min-h-[26rem] sm:min-h-[30rem] lg:min-h-[34rem]">
        <div className="absolute inset-y-0 right-0 w-full md:w-[78%]">
          <AssetImage file={card.cover} alt="" className="size-full object-cover object-[70%_center]" />
        </div>
        <div className="absolute inset-0" style={{ backgroundImage: 'var(--hero-shade)' }} aria-hidden />
        {/* Anchored at the top: the description and cast arrive after the card, and only push what is below them. */}
        <div className="relative flex min-h-[26rem] max-w-2xl animate-rise flex-col justify-start gap-4 px-4 pb-10 pt-20 sm:min-h-[30rem] sm:px-6 sm:pt-24 lg:min-h-[34rem] lg:px-8 lg:pb-14 lg:pt-28">
          <p className="font-mono text-sm font-semibold tracking-wide text-primary">{card.code}</p>
          <h1 className="font-display line-clamp-3 text-3xl leading-tight text-foreground drop-shadow-sm sm:text-4xl lg:text-5xl">{card.title}</h1>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground/80">
            {facts.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
            {genres.map((genre) => (
              <Link key={genre.termId} to={urls.term(genre.termId)} className="rounded-button border border-foreground/25 px-2 py-0.5 text-xs transition-theme hover:border-primary hover:text-primary">
                {genre.name}
              </Link>
            ))}
          </p>
          {detail === undefined ? (
            <Skeleton className="h-[4.5rem] w-full max-w-lg" />
          ) : detail.description === undefined ? null : (
            <p className="line-clamp-3 max-w-xl text-sm leading-relaxed text-foreground/80 sm:text-base">{detail.description}</p>
          )}
          {detail === undefined || detail.cast.length === 0 ? null : <p className="truncate text-sm text-muted-foreground">With {detail.cast.map((person) => person.name).join(', ')}</p>}
          <div className="flex flex-wrap gap-3 pt-2">
            <Button asChild variant="cta" size="hero">
              <Link to={`${urls.movie(card.code)}?play=1`}>
                <Play className="fill-current" />
                {resuming ? `Resume at ${formatRuntime(card.progress?.positionS) ?? ''}` : 'Play'}
              </Link>
            </Button>
            <Button asChild variant="glass" size="hero">
              <Link to={urls.movie(card.code)}>
                <Info />
                More info
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
